import { ActionIcon, Box, Collapse, Menu, Text } from '@mantine/core';
import { PatientContact } from '@medplum/fhirtypes';
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { UnifiedList } from '../shared/UnifiedList';
import { RelatedContactItem } from './RelatedContactItem';
import { PatientContactWithRelatedPerson } from './RelatedContactsCard';
import { ContactActions } from './services';
import { ORBIT_GRAY } from '@/theme/colors';

export type RelatedContactListItem = {
  contact: PatientContact;
  id: string;
  actions: ContactActions;
};

type RelatedContactListProps = {
  items: PatientContactWithRelatedPerson[];
  actionsFor: (item: PatientContactWithRelatedPerson) => ContactActions;
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  editingId: string | null;
  renderEditForm: (id: string, onClose: () => void) => ReactNode;
};

export function RelatedContactList({
  items,
  actionsFor,
  onRemove,
  onEdit,
  editingId,
  renderEditForm,
}: RelatedContactListProps) {
  const listItems: RelatedContactListItem[] = items.map((item) => ({
    contact: item,
    id: item.id,
    actions: actionsFor(item),
  }));

  if (items.length === 0) {
    return (
      <Text c={ORBIT_GRAY} size="sm" ta="center" py="md">
        No related contacts
      </Text>
    );
  }

  return (
    <UnifiedList
      items={listItems}
      gap={8}
      renderItem={(item) => {
        return <RelatedContactItem contact={item.contact} policyholder={item.actions.policyholder} />;
      }}
      renderActions={(item) => {
        const { edit, remove } = item.actions;
        if (!edit && !remove) return null;
        return (
          <Menu withinPortal position="bottom-end" shadow="lg" radius="lg">
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" color="gray" aria-label="Contact actions">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {edit && (
                <Menu.Item
                  leftSection={<IconEdit size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item.id);
                  }}
                >
                  Edit
                </Menu.Item>
              )}
              {remove && (
                <Menu.Item
                  leftSection={<IconTrash size={16} />}
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.id);
                  }}
                >
                  Remove
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        );
      }}
      renderAdditionalContent={(item) => {
        const isEditing = editingId === item.id;

        return (
          <>
            {isEditing && (
              <Collapse in={isEditing}>
                <Box mt={8} w="100%">
                  {renderEditForm(item.id, () => onEdit(item.id))}
                </Box>
              </Collapse>
            )}
          </>
        );
      }}
    />
  );
}
